<script lang="ts">
  import { route, goto } from '@mateothegreat/svelte5-router'
  import { currentPath, servers } from '$lib/store'

  import * as DropdownMenu from '$lib/components/ui/dropdown-menu/index.js'
  import * as AlertDialog from '$lib/components/ui/alert-dialog/index.js'
  import { buttonVariants } from '$lib/components/ui/button/index.js'
  import { deleteLibrary } from '$lib/apis/library/api'

  import { cn } from '$lib/utils.js'
  import BooksIcon from '$lib/assets/icons/BooksIcon.svelte'
  import ComicsIcon from '$lib/assets/icons/ComicsIcon.svelte'
  import WebnovelIcon from '$lib/assets/icons/WebnovelIcon.svelte'
  import WebtoonIcon from '$lib/assets/icons/WebtoonIcon.svelte'
  import DocsIcon from '$lib/assets/icons/DocsIcon.svelte'
  import EllipsisVertical from '@lucide/svelte/icons/ellipsis-vertical'

  let { item, props, lib, libraries } = $props()

  let mouseFocus = $state(false)
  let menuOpen = $state(false)
  let confirmOpen = $state(false)
  let deleting = $state(false)

  const focus = $derived(mouseFocus || menuOpen)
  const active = $derived($currentPath.includes(item.uid) && $currentPath.includes(lib.id))
  const kind = $derived(String(lib.kind ?? '').toUpperCase())

  async function confirmDelete() {
    if (deleting) return
    deleting = true
    try {
      const server = ($servers as Record<string, unknown>)[item.uid]
      await deleteLibrary(server, lib.id)
      if (libraries?.[item.uid]) {
        libraries[item.uid] = libraries[item.uid].filter((l: { id: string }) => l.id !== lib.id)
      }
      confirmOpen = false
      if ($currentPath.includes(lib.id)) {
        goto(`/media/${item.uid}`)
      }
    } catch (err) {
      console.error('delete library failed', err)
    } finally {
      deleting = false
    }
  }
</script>

<div
  class="relative flex w-full items-center"
  onmouseenter={() => (mouseFocus = true)}
  onmouseleave={() => (mouseFocus = false)}
>
  {#if active}
    <span
      class="absolute -start-2.5 top-1/2 z-10 h-5 w-0.5 -translate-x-px -translate-y-1/2 rounded-full bg-latte-green dark:bg-macchiato-green"
      aria-hidden="true"
    ></span>
  {/if}
  <a
    href="/media/{item.uid}/library/{lib.id}"
    {...props}
    class={cn(
      'flex min-w-0 flex-1 items-center gap-2 pe-7 text-latte-text dark:text-macchiato-text',
      active && 'text-latte-green dark:text-macchiato-green',
      props?.class,
    )}
    use:route
  >
    <span class="flex size-7 shrink-0 items-center justify-center">
      {#if kind === 'NOVEL'}
        <BooksIcon
          size="lg"
          class={active ? 'text-latte-green dark:text-macchiato-green' : 'opacity-70'}
        />
      {:else if kind === 'MANGA'}
        <ComicsIcon
          size="lg"
          class={active ? 'text-latte-green dark:text-macchiato-green' : 'opacity-70'}
        />
      {:else if kind === 'WEBNOVEL'}
        <WebnovelIcon
          size="lg"
          class={active ? 'text-latte-green dark:text-macchiato-green' : 'opacity-70'}
        />
      {:else if kind === 'WEBTOON'}
        <WebtoonIcon
          size="lg"
          class={active ? 'text-latte-green dark:text-macchiato-green' : 'opacity-70'}
        />
      {:else}
        <DocsIcon
          size="lg"
          class={active ? 'text-latte-green dark:text-macchiato-green' : 'opacity-70'}
        />
      {/if}
    </span>
    <span class="min-w-0 truncate text-sm {active ? 'font-medium' : ''}">{lib.name}</span>
  </a>

  <DropdownMenu.Root bind:open={menuOpen}>
    <DropdownMenu.Trigger
      class="absolute end-0.5 top-1/2 z-10 -translate-y-1/2 rounded-md p-1 transition-opacity
        text-latte-text/55 hover:bg-latte-surface1/50 hover:text-latte-text
        dark:text-macchiato-text/55 dark:hover:bg-macchiato-surface1/50 dark:hover:text-macchiato-text
        {focus ? 'opacity-100' : 'pointer-events-none opacity-0'}"
      onclick={(e) => {
        e.preventDefault()
        e.stopPropagation()
      }}
    >
      <EllipsisVertical class="size-3.5" />
      <span class="sr-only">Library options</span>
    </DropdownMenu.Trigger>
    <DropdownMenu.Content class="w-48 opacity-95" align="start">
      <DropdownMenu.Group>
        <DropdownMenu.Item
          onclick={(e) => {
            e.preventDefault()
            goto(`/media/${item.uid}/library/${lib.id}/settings`)
          }}
        >
          Settings
        </DropdownMenu.Item>
        <DropdownMenu.Separator />
        <DropdownMenu.Item
          variant="destructive"
          onclick={() => {
            confirmOpen = true
          }}
        >
          Delete Library
        </DropdownMenu.Item>
      </DropdownMenu.Group>
    </DropdownMenu.Content>
  </DropdownMenu.Root>
</div>

<AlertDialog.Root bind:open={confirmOpen}>
  <AlertDialog.Content>
    <AlertDialog.Header>
      <AlertDialog.Title>Delete library?</AlertDialog.Title>
      <AlertDialog.Description>
        This will permanently delete <strong>{lib.name}</strong> and its associated data. This
        action cannot be undone.
      </AlertDialog.Description>
    </AlertDialog.Header>
    <AlertDialog.Footer>
      <AlertDialog.Cancel disabled={deleting}>Cancel</AlertDialog.Cancel>
      <AlertDialog.Action
        class={buttonVariants({ variant: 'destructive' })}
        disabled={deleting}
        onclick={(e) => {
          e.preventDefault()
          confirmDelete()
        }}
      >
        {deleting ? 'Deleting…' : 'Delete Library'}
      </AlertDialog.Action>
    </AlertDialog.Footer>
  </AlertDialog.Content>
</AlertDialog.Root>
